'use client';

import { X } from 'phosphor-icons';
import { forwardRef, useMemo } from 'react';
import { c } from '../../../utils/tailwind-merge';
import type { FeedbackButtonProps } from './FeedbackButton.types';

export const FeedbackButton = forwardRef<
  HTMLButtonElement,
  FeedbackButtonProps
>(
  (
    { microcopy, onClose, className = '', vertical = false, ...restProps },
    ref
  ) => {
    const handleClose = (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onClose?.();
    };

    const wrapperBase = c(
      'flex items-start',
      'rounded-t-lg overflow-hidden select-none',
      vertical ? '-rotate-90' : '',
      'group',
      className
    );

    const sizeClasses = c(
      'w-[143px] h-9',
      'md:w-[143px] md:h-9',
      'lg:w-[143px] lg:h-9'
    );

    const bgClasses = c('bg-blue text-white', 'hover:bg-blue-hover');

    const prefersReducedMotion = useMemo(() => {
      if (typeof window === 'undefined' || !window.matchMedia) return false;
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }, []);

    const motionClasses = prefersReducedMotion
      ? ''
      : c(
          'transition-all duration-300',
          '[transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)]',
          'origin-bottom',
          'hover:h-[46px]'
        );

    const labelAreaClass = c(
      'flex items-start justify-start',
      'w-[105px] h-full',
      'pl-3 pr-0 pt-1.5',
      'font-bold uppercase tracking-[0.094rem] text-white',
      'select-none'
    );

    const containerGapClass = c('gap-0.5', 'h-full', 'items-start');

    const closeBtnClass = c(
      'inline-flex items-start justify-center',
      'w-9 h-full',
      'p-2.5'
    );

    const transformStyle = prefersReducedMotion
      ? { transition: 'none' }
      : undefined;

    return (
      <button
        ref={ref}
        type="button"
        aria-label={microcopy.buttonAriaLabel}
        className={c(wrapperBase, sizeClasses, bgClasses, motionClasses)}
        data-testid="feedback-button"
        style={transformStyle}
        {...restProps}
      >
        <div className={c('flex items-start', containerGapClass)}>
          <span
            className={c(
              'order-0 focus-outline bg-transparent',
              labelAreaClass
            )}
            data-testid="feedback-button-cta"
          >
            <span>{microcopy.cta}</span>
          </span>
          <span
            onClick={handleClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleClose(e);
              }
            }}
            aria-label={microcopy.closeAriaLabel}
            className={c('order-0 focus-outline bg-transparent', closeBtnClass)}
            data-testid="feedback-button-close"
            role="button"
            tabIndex={0}
          >
            <X size={16} weight="bold" aria-hidden="true" />
          </span>
        </div>
      </button>
    );
  }
);

FeedbackButton.displayName = 'FeedbackButton';
